import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { downloadCustomerCsv, getDistrictCustomerSiteInfo } from '../../Services/serviceURL';
import { OnboardedCustomerData } from './OnboardedCustomertable';

// Async thunks
export const fetchCustomerList = createAsyncThunk(
    'customer/fetchCustomerList',
    async (_, { rejectWithValue }) => {
        try {
            // Fetch customers using new API endpoint
            const districtCustomerData = await getDistrictCustomerSiteInfo();
            
            
            // Ensure we have an array to work with
            if (!Array.isArray(districtCustomerData)) {
                console.error('Expected array from getDistrictCustomerSiteInfo, received:', districtCustomerData);
                return [];
            }
            
            // Transform the API response to match OnboardedCustomerData interface
            const mappedData: OnboardedCustomerData[] = districtCustomerData.map((item, index) => ({
                customer: item.salesDistrictCustomer || 'N/A',
                cnxId: `CNX-${index + 1}`, // Generate a CNX ID since not provided
                soldTo: '', // Not provided in the new API response
                country: 'N/A', // Not provided in the new API response
                salesDistrict: 'N/A', // Not provided in the new API response
                salesRegion: 'N/A', // Not provided in the new API response
                state: 'N/A', // Not provided in the new API response
                city: 'N/A', // Not provided in the new API response
            }));

            return mappedData;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch customers');
        }
    }
);

export const downloadCustomers = createAsyncThunk(
    'customer/downloadCustomers',
    async (_, { rejectWithValue }) => {
        try {
            const response = await downloadCustomerCsv();
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to download customers');
        }
    }
);

// Interface for the slice state
interface CustomerState {
    customers: OnboardedCustomerData[];
    error: string | null;
    loading: boolean;
    downloadError: string | null;
    downloadLoading: boolean;
    createError: string | null;
    createLoading: boolean;
}

// Initial state
const initialState: CustomerState = {
    customers: [],
    error: null,
    loading: false,
    downloadError: null,
    downloadLoading: false,
    createError: null,
    createLoading: false
};

// Create the slice
const customerSlice = createSlice({
    name: 'customer',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
            state.downloadError = null;
            state.createError = null;
        },
        resetCustomers: (state) => {
            state.customers = [];
            state.error = null;
            state.loading = false;
            state.downloadError = null;
            state.downloadLoading = false;
            state.createError = null;
            state.createLoading = false;
        }
    },
    extraReducers: (builder) => {
        // Handle fetchCustomerList
        builder
            .addCase(fetchCustomerList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCustomerList.fulfilled, (state, action) => {
                state.loading = false;
                state.customers = action.payload;
                state.error = null;
            })
            .addCase(fetchCustomerList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || 'Failed to fetch customers';
            })
            // Handle downloadCustomers
            .addCase(downloadCustomers.pending, (state) => {
                state.downloadLoading = true;
                state.downloadError = null;
            })
            .addCase(downloadCustomers.fulfilled, (state) => {
                state.downloadLoading = false;
                state.downloadError = null;
            })
            .addCase(downloadCustomers.rejected, (state, action) => {
                state.downloadLoading = false;
                state.downloadError = action.payload as string || 'Failed to download CSV';
            })
    },
});

// Export actions
export const { clearError, resetCustomers } = customerSlice.actions;

// Export the reducer
export default customerSlice.reducer;

// Selectors
export const selectCustomers = (state: { customer: CustomerState }) => state.customer.customers;
export const selectCustomersLoading = (state: { customer: CustomerState }) => state.customer.loading;
export const selectCustomersError = (state: { customer: CustomerState }) => state.customer.error;
export const selectDownloadLoading = (state: { customer: CustomerState }) => state.customer.downloadLoading;
export const selectDownloadError = (state: { customer: CustomerState }) => state.customer.downloadError;
export const selectCreateLoading = (state: { customer: CustomerState }) => state.customer.createLoading;
export const selectCreateError = (state: { customer: CustomerState }) => state.customer.createError;